import { useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

export function Field(p: { label: string; error?: string | undefined; hint?: string; optional?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const { label, error, hint, optional, ...input } = p;
  const describedBy = [hint ? `${id}-hint` : "", error ? `${id}-err` : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      <label htmlFor={id}>{label}{optional && <span className="optional"> (optional)</span>}</label>
      {hint && <p className="hint" id={`${id}-hint`}>{hint}</p>}
      <input id={id} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...input} />
      {error && <p className="error-text" id={`${id}-err`}>{error}</p>}
    </div>
  );
}

export function Select(p: { label: string; error?: string | undefined; options: { value: string; label: string }[]; placeholder?: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  const { label, error, options, placeholder, ...sel } = p;
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <select id={id} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-err` : undefined} {...sel}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="error-text" id={`${id}-err`}>{error}</p>}
    </div>
  );
}

export function Button(p: { children: ReactNode; variant?: "primary" | "secondary" | "quiet"; busy?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const { variant = "primary", busy, children, disabled, ...rest } = p;
  return <button className={`btn btn-${variant}`} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>{busy ? "Please wait…" : children}</button>;
}

export function Notice({ tone, children }: { tone: "error" | "info" | "success"; children: ReactNode }) {
  return <div className={`notice notice-${tone}`} role={tone === "error" ? "alert" : "status"}>{children}</div>;
}

/** Split layout for sign-in screens: the organisation's (or product's) name carries the brand panel. */
export function AuthLayout({ title, subtitle, children, brand = "HealthOS" }: { title: string; subtitle?: string; children: ReactNode; brand?: string }) {
  return (
    <div className="auth">
      <aside className="auth-brand" aria-hidden="true">
        <span className="brand-mark">{brand}</span>
        <p className="brand-line">Patient records, appointments and billing for clinics and hospitals.</p>
      </aside>
      <main className="auth-main">
        <div className="auth-form">
          <h1>{title}</h1>
          {subtitle && <p className="lede">{subtitle}</p>}
          {children}
        </div>
      </main>
    </div>
  );
}

export function PasswordChecklist({ password, name, email }: { password: string; name?: string; email?: string | null }) {
  const parts = [email?.split("@")[0], ...(name?.split(/\s+/) ?? [])].filter((s): s is string => !!s && s.replace(/\W/g, "").length >= 4).map((s) => s.toLowerCase().replace(/\W/g, ""));
  const checks = [
    { ok: [...password].length >= 10, label: "At least 10 characters" },
    { ok: password.length > 0 && !parts.some((s) => password.toLowerCase().includes(s)), label: "Doesn't contain your name or email" },
  ];
  return (
    <ul className="checklist" aria-label="Password requirements">
      {checks.map((c) => <li key={c.label} className={c.ok ? "ok" : ""}><span aria-hidden="true">{c.ok ? "✓" : "○"}</span> {c.label}<span className="sr-only">{c.ok ? " (met)" : " (not met)"}</span></li>)}
    </ul>
  );
}

export const PASSWORD_PROBLEM_TEXT: Record<string, string> = {
  too_short: "Use at least 10 characters. A short sentence works well.",
  too_long: "Use 128 characters or fewer.",
  common: "That password is too common. Choose something less predictable.",
  contains_identity: "Don't include your name or email in the password.",
  breached: "This password has appeared in a data breach elsewhere. Choose a different one.",
};

export function TextArea(p: { label: string; error?: string | undefined; hint?: string; optional?: boolean } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  const { label, error, hint, optional, ...ta } = p;
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      <label htmlFor={id}>{label}{optional && <span className="optional"> (optional)</span>}</label>
      {hint && <p className="hint" id={`${id}-hint`}>{hint}</p>}
      <textarea id={id} rows={3} aria-invalid={error ? true : undefined} aria-describedby={[hint ? `${id}-hint` : "", error ? `${id}-err` : ""].filter(Boolean).join(" ") || undefined} {...ta} />
      {error && <p className="error-text" id={`${id}-err`}>{error}</p>}
    </div>
  );
}
